import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { CAT_MAP } from "@/lib/categories";
import { TrendChart, TrendDelta } from "@/components/ValueTrend";
import { TrendingUp, Info, ChevronLeft, ArrowRight } from "lucide-react";
import { Seo, breadcrumbLd, clip, SITE_URL, SITE_NAME } from "@/components/Seo";

function faqFor(card) {
  const live = card.value_source === "ebay-live" ? card.live : null;
  const items = [
    [`Quanto vale ${card.name}?`,
      live
        ? `La fascia è ${card.value_range}, con una mediana di ${Number(live.median).toLocaleString("it-IT")} € calcolata sui prezzi richiesti da ${live.count} inserzioni in vendita ora su eBay. Sono prezzi richiesti dai venditori, non vendite concluse né una perizia.`
        : `La fascia indicativa è ${card.value_range}. Si tratta di una stima elaborata dalla nostra redazione con l'ausilio dell'AI, non di una quotazione ufficiale né di una perizia.`],
  ];
  if (card.factors?.length) {
    items.push([`Da cosa dipende il prezzo di ${card.name}?`,
      `I fattori che pesano di più sono: ${card.factors.join("; ")}.`]);
  }
  if (card.advice) items.push([`Conviene comprarlo oggi?`, card.advice]);
  items.push(["Questa valutazione ha valore legale?",
    "No. È un'indicazione editoriale pensata per orientarsi: per una vendita, un'assicurazione o una successione serve una perizia di un professionista o una certificazione di un ente di grading."]);
  return items;
}

export default function ValueCardPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/value-cards/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center font-editorial text-2xl text-stone-800">
      <Seo title="Scheda non trovata" noindex />
      Scheda valore non trovata
    </div>
  );
  if (!data) return <div className="max-w-3xl mx-auto px-4 py-24 animate-pulse"><div className="h-12 rounded bg-panel mb-6" /><div className="h-64 rounded-xl bg-panel" /></div>;

  const { card, related, articles } = data;
  const cat = CAT_MAP[card.category];
  const path = `/valori/${card.slug}`;
  const faq = faqFor(card);
  const live = card.value_source === "ebay-live" ? card.live : null;
  const mainFactor = (card.factors?.[0] || "").split(":")[0].trim();
  const mainFactorPhrase = mainFactor ? mainFactor.charAt(0).toLowerCase() + mainFactor.slice(1) : "";

  return (
    <article className="max-w-3xl mx-auto px-4 py-10 fade-up" data-testid="value-card-page">
      <Seo
        title={`Quanto vale ${card.name}? Valore e prezzi indicativi`}
        description={clip(`${card.name}: fascia di valore ${card.value_range}. Cosa incide sul prezzo, andamento della stima e consigli pratici prima di comprare o vendere.`, 185)}
        path={path}
        image={card.image_url}
        imageAlt={card.name}
        type="article"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Schede Valore", path: "/valori" },
            { name: `Quanto vale ${card.name}`, path },
          ]),
          {
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: faq.map(([q, a]) => ({
              "@type": "Question", name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          },
          {
            "@context": "https://schema.org", "@type": "Article",
            headline: `Quanto vale ${card.name}?`.slice(0, 110),
            inLanguage: "it-IT",
            dateModified: card.last_changed_at || card.created_at,
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
            author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          },
        ]}
      />

      <Link to="/valori" data-testid="back-to-values" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors">
        <ChevronLeft size={15} /> Tutte le Schede Valore
      </Link>

      <div className="flex items-center gap-3 mt-5 flex-wrap">
        <Link to={`/categoria/${card.category}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-meta font-semibold uppercase tracking-[0.12em] border rounded-full ${cat?.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cat?.dot}`} />{cat?.name}
        </Link>
        {card.era && <span className="text-[11px] font-meta text-stone-500">{card.era}</span>}
      </div>

      <h1 className="mt-4 font-editorial font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-stone-900">
        Quanto vale <span className="mark-hl">{card.name}</span>?
      </h1>

      <div className="mt-6 rounded-2xl bg-panel border-2 border-line p-5 sticker-soft" data-testid="value-answer">
        <div className="flex items-center gap-1.5 text-[9px] font-meta uppercase tracking-[0.18em] text-stone-500 mb-2">
          <TrendingUp size={11} className="text-gold" /> Risposta rapida · {live ? "prezzi eBay di oggi" : "stima AI indicativa"}
        </div>
        <p className="font-meta font-bold text-lg md:text-lg text-gold">{card.value_range}</p>
        <p className="text-sm text-stone-600 mt-3 leading-relaxed">
          {live ? (
            <>Questa è la fascia centrale dei prezzi richiesti <strong className="text-stone-800">in questo momento</strong> su
              eBay per <strong className="text-stone-800">{card.name}</strong>: mediana {Number(live.median).toLocaleString("it-IT")} €
              su {live.count} inserzioni in vendita{live.marketplace === "EBAY_IT" ? " in Italia" : " all'estero"}. Sono prezzi
              richiesti dai venditori, non vendite concluse.</>
          ) : (
            <>Questa è la fascia in cui, secondo la nostra stima, si colloca oggi <strong className="text-stone-800">{card.name}</strong>
              {card.era ? ` (${card.era})` : ""}. Lo scarto tra minimo e massimo è normale: nel collezionismo
              {card.factors?.length ? ` conta soprattutto ${mainFactorPhrase}` : " conta soprattutto lo stato di conservazione"}.</>
          )}
          {card.history?.length >= 2 && " Qui sotto trovi anche come si è mosso il valore nel tempo."}
        </p>
      </div>

      {card.factors?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">Cosa fa salire o crollare il prezzo</h2>
          <ul className="mt-3 space-y-2">
            {card.factors.map((f, i) => (
              <li key={i} className="text-stone-600 flex gap-2.5 leading-relaxed">
                <span className="font-meta text-xs mt-1" style={{ color: cat?.hex }}>0{i + 1}</span>{f}
              </li>
            ))}
          </ul>
        </section>
      )}

      {card.advice && (
        <section className="mt-10">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">Il consiglio della redazione</h2>
          <p className="mt-3 text-stone-700 rounded-2xl bg-ink border-2 border-line p-4 leading-relaxed">{card.advice}</p>
        </section>
      )}

      {card.history?.length >= 1 && (
        <section className="mt-10" data-testid="value-history">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">
              {live ? "Andamento dei prezzi" : "Andamento della nostra stima"}
            </h2>
            {card.history.length >= 2 && <TrendDelta history={card.history} />}
          </div>
          <div className="mt-3">
            <TrendChart history={card.history} hex={cat?.hex || "#E5C158"} id={`vp-${card.id}`} height={190} />
          </div>
          <p className="text-[11px] text-stone-400 mt-2">
            {live
              ? "Un punto per ogni rilevamento: la mediana dei prezzi richiesti su eBay all'ora indicata. Sono prezzi di vendita richiesti, non aggiudicazioni."
              : "Un punto per ogni revisione della stima, registrato da noi: non sono quotazioni di mercato né prezzi di vendita reali."}
          </p>
        </section>
      )}

      <section className="mt-12" data-testid="value-faq">
        <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">Domande frequenti</h2>
        <div className="mt-4 space-y-3">
          {faq.map(([q, a]) => (
            <details key={q} className="rounded-2xl bg-panel border-2 border-line p-4 group">
              <summary className="cursor-pointer font-semibold text-stone-900 text-sm marker:text-gold">{q}</summary>
              <p className="mt-2.5 text-sm text-stone-600 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {card.note && (
        <p className="mt-8 text-[11px] text-stone-400 flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 shrink-0" />{card.note}
        </p>
      )}

      {related?.length > 0 && (
        <section className="mt-12">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">Altre schede di {cat?.name}</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/valori/${r.slug}`} data-testid={`related-card-${r.slug}`}
                className="rounded-2xl bg-panel border-2 border-line p-4 lift">
                <span className="block font-semibold text-sm text-stone-900">Quanto vale {r.name}</span>
                <span className="block font-meta text-[11px] text-gold mt-1">{r.value_range}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {articles?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">Ultime notizie sul tema</h2>
          <ul className="mt-3 space-y-2">
            {articles.map((a) => (
              <li key={a.slug}>
                <Link to={`/articolo/${a.slug}`} className="text-stone-600 hover:text-stone-900 transition-colors inline-flex items-start gap-2">
                  <ArrowRight size={14} className="mt-1 shrink-0 text-gold" />{a.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-12 rounded-2xl bg-ink-deep border-2 border-dashed border-line p-5">
        <p className="text-sm text-stone-600 leading-relaxed">
          Non sai da dove iniziare? Leggi la{" "}
          <Link to="/collezionismo" data-testid="link-pillar" className="font-semibold text-stone-900 underline decoration-gold decoration-2 underline-offset-2">
            guida al collezionismo per principianti
          </Link>{" "}
          oppure sfoglia le <Link to={`/categoria/${card.category}`} className="font-semibold text-stone-900 underline decoration-gold decoration-2 underline-offset-2">novità su {cat?.name.toLowerCase()}</Link>.
        </p>
      </div>
    </article>
  );
}
